"use client";

import { m, useInView } from "framer-motion";
import { useRef } from "react";
import { TimelineEvent } from "./types";

type Props = {
  event: TimelineEvent;
  index: number;
  onItemClick: (item: TimelineEvent) => void;
};

export default function TimelineItem({ event, index, onItemClick }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const isEven = index % 2 === 0;
  const isHighlighted = index % 2 === 0;
  const isFeatured = Number(event.is_featured) === 1;

  const variants = {
    hidden: { opacity: 0, y: 40, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const monthName = event.month ? MONTHS[Number(event.month) - 1] : null;
  const dayNum = event.day ? String(event.day) : null;

  // API has month/day as numbers (e.g. month:2, day:28) — build full date from those
  const displayDate =
    [dayNum, monthName, event.year].filter(Boolean).join(" ") || event.year || "—";

  const handleClick = () => {
    if (isFeatured) onItemClick(event);
  };

  return (
    <m.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="relative"
    >
      {/* ── MOBILE LAYOUT ─────────────────────────────────────────── */}
      <div className="flex md:hidden flex-row items-stretch gap-0 mt-3">
        <div className="flex flex-col items-center mr-4 flex-shrink-0 relative">
          <div className="md:block hidden w-[2px] flex-1 bg-[#E6EAF2] min-h-[12px]" />
          <m.div
            className="absolute left-[-6px] top-1 w-4 h-4 rounded-full bg-[#EFF1F5] border-[3px] border-[#195AFF] shadow-md z-10 flex-shrink-0"
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : { scale: 0 }}
            transition={{ duration: 0.35, delay: 0.18 }}
          />
          <div className="md:block hidden w-[2px] flex-1 bg-[#E6EAF2]" />
        </div>

        <div className="flex flex-col gap-2 pb-8 flex-1 min-w-0">
          <div className="flex items-center" style={{ marginTop: "2px" }}>
            <span className="inline-block bg-white border border-[#E6EAF2] shadow-sm px-3 py-1 rounded-md text-[12px] font-semibold text-[#0B1B3A] font-Schibsted_Grotesk">
              {displayDate}
            </span>
          </div>

          {/* ✅ onClick added */}
          <m.div
            onClick={handleClick}
            style={{ cursor: isFeatured ? "pointer" : "default" }}
            className={`relative w-full bg-white border border-[#E6EAF2] rounded-[8px] p-4 overflow-hidden
              ${isHighlighted ? "drop-shadow-[0_0_8px_rgba(0,0,0,0.18)]" : "shadow-[0_4px_14px_rgba(34,60,80,0.14)]"}
              ${isFeatured ? "hover:ring-2 hover:ring-[#195AFF]/40 transition-all" : ""}`}
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {event.image && (
              <m.img
                src={event.image}
                alt={event.title}
                className="w-full h-36 object-cover rounded-md mb-3"
                initial={{ opacity: 0, scale: 1.05 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.7 }}
              />
            )}
            <h3 className="text-[#0B1B3A] font-semibold text-[15px] mb-1 leading-snug font-NeueMontreal">
              {event.title}
            </h3>
            <p className="text-[#6B768A] text-[13px] leading-relaxed line-clamp-4 font-Schibsted_Grotesk">
              {event.description}
            </p>
            {isFeatured && (
              <p className="mt-2 text-[#195AFF] text-[12px] font-semibold font-Schibsted_Grotesk">Read more →</p>
            )}
            {isHighlighted && (
              <div className="absolute left-0 bottom-0 w-full h-[3px] bg-[#195AFF] rounded-b-[8px]" />
            )}
          </m.div>
        </div>
      </div>

      {/* ── DESKTOP LAYOUT ────────────────────────────────────────── */}
      <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center gap-10">
        {/* LEFT SIDE */}
        <div className="flex justify-end pr-6">
          {!isEven && (
            // ✅ onClick added
            <div
              onClick={handleClick}
              style={{ cursor: isFeatured ? "pointer" : "default" }}
              className={`relative w-full max-w-[551px] bg-white border border-[#E6EAF2] rounded-[6px] p-5
                ${isHighlighted ? "filter drop-shadow-[0_0_8px_rgba(0,0,0,0.25)]" : "shadow-[0_6px_18px_rgba(34,60,80,0.18)]"}
                ${isFeatured ? "hover:ring-2 hover:ring-[#195AFF]/40 transition-all" : ""}`}
            >
              <div className="absolute right-[-10px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-l-[10px] border-l-white" />
              <div className="absolute right-[-11px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[11px] border-t-transparent border-b-[11px] border-b-transparent border-l-[11px] border-l-[#E6EAF2] -z-10" />
              <div className="flex flex-col gap-4">
                {event.image && (
                  <div className="h-[84px] w-[110px] overflow-hidden rounded-md border border-[#E6EAF2]">
                    <m.img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover"
                      initial={{ opacity: 0, scale: 1.06 }}
                      animate={isInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.7, delay: 0.15 }}
                    />
                  </div>
                )}
                <div>
                  <h3 className="text-[#0B1B3A] font-semibold text-[16px] mb-2 font-NeueMontreal">{event.title}</h3>
                  <p className="text-[#6B768A] text-[13px] leading-relaxed line-clamp-3 font-Schibsted_Grotesk">{event.description}</p>
                  {isFeatured && (
                    <p className="mt-2 text-[#195AFF] text-[12px] font-semibold font-Schibsted_Grotesk">Read more →</p>
                  )}
                  {isHighlighted && (
                    <div className="absolute left-0 bottom-0 w-full h-[3px] bg-[#195AFF] rounded-b-[10px]" />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CENTER NODE + DATE PILL */}
        <div className="relative flex items-center justify-center">
          <div className={`absolute top-1/2 -translate-y-1/2 ${isEven ? "right-[54px]" : "left-[54px]"}`}>
            <div className="relative bg-white border border-[#E6EAF2] shadow px-4 py-2 rounded-md text-[13px] font-semibold text-[#0B1B3A] whitespace-nowrap font-Schibsted_Grotesk">
              {displayDate}
              <div className={`absolute top-1/2 -translate-y-1/2 w-0 h-0 ${isEven ? "right-[-10px] border-t-[8px] border-b-[8px] border-l-[10px] border-t-transparent border-b-transparent border-l-white" : "left-[-10px] border-t-[8px] border-b-[8px] border-r-[10px] border-t-transparent border-b-transparent border-r-white"}`} />
              <div className={`absolute top-1/2 -translate-y-1/2 w-0 h-0 -z-10 ${isEven ? "right-[-11px] border-t-[9px] border-b-[9px] border-l-[11px] border-t-transparent border-b-transparent border-l-[#E6EAF2]" : "left-[-11px] border-t-[9px] border-b-[9px] border-r-[11px] border-t-transparent border-b-transparent border-r-[#E6EAF2]"}`} />
            </div>
          </div>
          <m.div
            className="w-5 h-5 rounded-full bg-[#EFF1F5] border-[3px] border-[#195AFF] shadow-md z-10"
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : { scale: 0 }}
            transition={{ duration: 0.35, delay: 0.18 }}
          />
        </div>

        {/* RIGHT SIDE */}
        <div className="flex justify-start pl-6">
          {isEven && (
            // ✅ onClick added
            <m.div
              onClick={handleClick}
              style={{ cursor: isFeatured ? "pointer" : "default" }}
              className={`relative w-full max-w-[520px] bg-white border border-[#E6EAF2] rounded-[6px] p-5
                ${isHighlighted ? "filter drop-shadow-[0_0_8px_rgba(0,0,0,0.25)]" : "shadow-[0_6px_18px_rgba(34,60,80,0.18)]"}
                ${isFeatured ? "hover:ring-2 hover:ring-[#195AFF]/40 transition-all" : ""}`}
              initial={{ opacity: 0, x: 40 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.25 }}
            >
              <div className="absolute left-[-10px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-r-[10px] border-r-white" />
              <div className="absolute left-[-11px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[11px] border-t-transparent border-b-[11px] border-b-transparent border-r-[11px] border-r-[#E6EAF2] -z-10" />
              <div className="flex flex-col gap-4">
                {event.image && (
                  <div className="h-full w-[110px] overflow-hidden rounded-md border border-[#E6EAF2]">
                    <m.img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-contain"
                      initial={{ opacity: 0, scale: 1.06 }}
                      animate={isInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.7, delay: 0.15 }}
                    />
                  </div>
                )}
                <div>
                  <h3 className="text-[#0B1B3A] font-semibold text-[16px] mb-2 font-NeueMontreal">{event.title}</h3>
                  <p className="text-[#6B768A] text-[13px] leading-relaxed line-clamp-3 font-Schibsted_Grotesk">{event.description}</p>
                  {isFeatured && (
                    <p className="mt-2 text-[#195AFF] text-[12px] font-semibold font-Schibsted_Grotesk">Read more →</p>
                  )}
                  {isHighlighted && (
                    <div className="absolute left-0 bottom-0 w-full h-[3px] bg-[#195AFF] rounded-b-[10px]" />
                  )}
                </div>
              </div>
            </m.div>
          )}
        </div>
      </div>
    </m.div>
  );
}